import { useState, useEffect, useRef } from 'react';

/**
 * useTypingPlaceholder
 * Cycles through `phrases` with a typewriter type + erase animation.
 *
 * @param {string[]} phrases   - Array of placeholder strings to cycle through
 * @param {number}   typeSpeed - Milliseconds per character while typing   (default 80)
 * @param {number}   eraseSpeed- Milliseconds per character while erasing  (default 40)
 * @param {number}   pauseMs   - Milliseconds to pause after full phrase   (default 1800)
 * @returns {string} current placeholder text
 */
const useTypingPlaceholder = (
    phrases = [],
    typeSpeed = 80,
    eraseSpeed = 40,
    pauseMs = 1800
) => {
    const [displayed, setDisplayed] = useState('');
    const phraseIndex = useRef(0);
    const charIndex = useRef(0);
    const isErasing = useRef(false);
    const timeoutRef = useRef(null);

    useEffect(() => {
        if (!phrases.length) return;

        const tick = () => {
            const current = phrases[phraseIndex.current];

            if (!isErasing.current) {
                // Typing forward
                if (charIndex.current < current.length) {
                    charIndex.current += 1;
                    setDisplayed(current.slice(0, charIndex.current));
                    timeoutRef.current = setTimeout(tick, typeSpeed);
                } else {
                    // Fully typed — pause then start erasing
                    timeoutRef.current = setTimeout(() => {
                        isErasing.current = true;
                        tick();
                    }, pauseMs);
                }
            } else {
                // Erasing backward
                if (charIndex.current > 0) {
                    charIndex.current -= 1;
                    setDisplayed(current.slice(0, charIndex.current));
                    timeoutRef.current = setTimeout(tick, eraseSpeed);
                } else {
                    // Fully erased — move to next phrase
                    isErasing.current = false;
                    phraseIndex.current = (phraseIndex.current + 1) % phrases.length;
                    timeoutRef.current = setTimeout(tick, typeSpeed * 2);
                }
            }
        };

        timeoutRef.current = setTimeout(tick, typeSpeed);

        return () => clearTimeout(timeoutRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phrases]);

    return displayed;
};

export default useTypingPlaceholder;
